import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  Description as ApplicationIcon,
  Payment as PaymentIcon,
  Assessment as ReportIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

interface QuickAction {
  title: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  action: () => void;
}

interface QuickActionsProps {
  onRefresh?: () => void;
}

const QuickActions: React.FC<QuickActionsProps> = ({ onRefresh }) => {
  const navigate = useNavigate();

  const actions: QuickAction[] = [
    {
      title: 'New Customer',
      description: 'Register a new customer',
      icon: <PersonAddIcon sx={{ fontSize: 32 }} />,
      color: '#1976d2',
      action: () => navigate('/customers?action=new'),
    },
    {
      title: 'New Application',
      description: 'Create loan application',
      icon: <ApplicationIcon sx={{ fontSize: 32 }} />,
      color: '#2e7d32',
      action: () => navigate('/loans/applications?action=new'),
    },
    {
      title: 'Record Payment',
      description: 'Record a payment',
      icon: <PaymentIcon sx={{ fontSize: 32 }} />,
      color: '#ed6c02',
      action: () => navigate('/payments?action=new'),
    },
    {
      title: 'Generate Report',
      description: 'View reports',
      icon: <ReportIcon sx={{ fontSize: 32 }} />,
      color: '#9c27b0',
      action: () => navigate('/reports'),
    },
  ];

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h6" fontWeight="bold">
              Quick Actions
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Common tasks and shortcuts
            </Typography>
          </Box>
          {onRefresh && (
            <Button
              size="small"
              startIcon={<RefreshIcon />}
              onClick={onRefresh}
              variant="outlined"
            >
              Refresh Data
            </Button>
          )}
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
            },
            gap: 2,
          }}
        >
          {actions.map((action, index) => (
            <Box
              key={index}
              onClick={action.action}
              sx={{
                p: 2,
                border: '2px solid',
                borderColor: 'divider',
                borderRadius: 2,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  borderColor: action.color,
                  backgroundColor: `${action.color}08`,
                  transform: 'translateY(-2px)',
                  boxShadow: 3,
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <Box
                  sx={{
                    mr: 2,
                    p: 1.5,
                    borderRadius: 2,
                    backgroundColor: `${action.color}15`,
                    color: action.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {action.icon}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    {action.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {action.description}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};

export default QuickActions;
